import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { LegalLayout } from "@/components/LegalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSiteSettings } from "@/lib/useSiteSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["General enquiry", "Account support", "Report a user", "Report an opportunity", "Privacy/data request", "Business enquiry", "Creator enquiry", "Technical issue", "Other"];

export default function Contact() {
  const [params] = useSearchParams();
  const settings = useSiteSettings();
  const [form, setForm] = useState({ name: "", email: "", category: params.get("category") || "General enquiry", message: "", website: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { toast.error("Please add your name and email"); return; }
    if (form.message.trim().length < 10) { toast.error("Please include a little more detail"); return; }
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <LegalLayout
      metaTitle="Contact Nearfluence"
      metaDescription="Get in touch with the Nearfluence team — general enquiries, account support, reporting, privacy requests and more."
      title="Contact us"
      subtitle="We're here to help. Choose a category and send us a message."
    >
      {sent ? (
        <div data-testid="contact-success" className="flex flex-col items-center rounded-3xl border border-emerald-200 bg-emerald-50 p-10 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          <h2 className="mt-4 font-display text-xl font-bold text-zinc-900">Message sent</h2>
          <p className="mt-1 text-sm text-zinc-600">Thanks for reaching out — we'll get back to you as soon as we can.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="rounded-3xl border border-zinc-200 bg-white p-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Your name</Label><Input data-testid="contact-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5 h-11" placeholder="Jane Smith" /></div>
            <div><Label>Email</Label><Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 h-11" placeholder="you@example.com" /></div>
          </div>
          <div className="mt-4"><Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="contact-category" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="mt-4"><Label>Message</Label><Textarea data-testid="contact-message" rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5" placeholder="How can we help?" /></div>
          {/* Honeypot field — hidden from real users */}
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden="true" />
          <Button type="submit" data-testid="contact-submit" disabled={busy} className="mt-5 h-11 w-full rounded-full bg-emerald-600 hover:bg-emerald-700 sm:w-auto sm:px-8">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send message"}
          </Button>
          {settings?.support_email && (
            <p className="mt-4 flex items-center gap-1.5 text-sm text-zinc-500"><Mail className="h-4 w-4" /> You can also email us at <a href={`mailto:${settings.support_email}`} className="font-medium text-emerald-600 hover:underline">{settings.support_email}</a></p>
          )}
        </form>
      )}
    </LegalLayout>
  );
}
